import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Loader } from '../../components/Loader/Loader'
import { useMainCarouselData } from '../../hooks/apiHooks/MentorHooks'
import { uploadMarketingMedia, useUpdateCarouselData } from '../api/apiHooks'
import { BaseHeader } from '../components/BaseHeader'
import {
  CarouselEditCard,
  SortableCarouselEditCard,
} from '../components/CarouselEditCard'
import { CarouselItem } from '../components/CarouselForm'
import { DeleteModal } from '../components/DeleteModal'
import { compressImage } from './utils'

export function CarouselEditPage() {
  const queryClient = useQueryClient()
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {
    data: slides,
    isLoading: isLoadingSlides,
    isFetching: isFetchingSlides,
  } = useMainCarouselData()
  const { mutateAsync, isPending } = useUpdateCarouselData()
  const [data, setData] = useState<CarouselItem[]>([])
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [itemToDelete, setItemToDelete] = useState<CarouselItem | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [activeId, setActiveId] = useState<string | null>(null)

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  useMemo(() => {
    if (slides?.items && !isFetchingSlides) {
      setData(slides.items as CarouselItem[])
    }
  }, [slides?.items, isFetchingSlides])

  useEffect(() => {
    setIsLoading(isLoadingSlides || isFetchingSlides || isPending)
  }, [isLoadingSlides, isFetchingSlides, isPending])

  // Handle adding a new empty item
  const handleAddItem = () => {
    const newItem: CarouselItem = {
      id: `new-item-${Date.now()}`,
      title: '',
      sourceType: 'Banner',
      backgroundImageUrl: '',
      visibility: 'admin',
      isNewEntry: true,
    }
    setData(prevData => [newItem, ...prevData])
  }

  // Handle item update
  const handleItemUpdate = async (updatedData: CarouselItem) => {
    try {
      setIsLoading(true)

      // uploadImage delete the image file if it exists and update the URL
      if (updatedData.backgroundImageFile) {
        if (updatedData.title.trim()) {
          const file = updatedData.backgroundImageFile
          const compressedFile = await compressImage(file, 0.3)
          const url = await uploadMarketingMedia(compressedFile)
          updatedData.backgroundImageUrl = url || ''
        } else {
          toast.error('Title is not valid')
          return
        }
        delete updatedData?.backgroundImageFile
      }

      // Upload video file if it exists and update the URL
      if (updatedData.backgroundVideoFile) {
        if (updatedData.title.trim()) {
          const file = updatedData.backgroundVideoFile
          const url = await uploadMarketingMedia(file)
          updatedData.backgroundVideoUrl = url || ''
        } else {
          toast.error('Title is not valid')
          return
        }
        delete updatedData?.backgroundVideoFile
      }
      delete updatedData?.backupBackgroundImageUrl

      const _updatedData = data
        .map(item => (item?.id === updatedData?.id ? updatedData : item))
        .filter(item => {
          if (
            !item.isNewEntry ||
            (item.title.trim() && item.backgroundImageUrl)
          ) {
            delete item.isNewEntry
            return true
          }
          return false
        })

      const res = await mutateAsync({
        items: _updatedData,
        updated_at: slides?.updated_at,
      })

      await queryClient.invalidateQueries({
        queryKey: ['get-main-carousel-data'],
      })
      toast.success(res?.message)
    } catch (err) {
      const error = err as AxiosError<Record<string, string>>
      console.error('Error updating item:', error)
      toast.error(
        error.response?.data?.message ||
          'Failed to update item. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Handle item deletion
  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const updatedItem = data.filter(item => item?.id !== itemToDelete?.id)
        if (!itemToDelete?.isNewEntry) {
          await mutateAsync({
            items: updatedItem,
            updated_at: slides?.updated_at,
          })

          await queryClient.invalidateQueries({
            queryKey: ['get-main-carousel-data'],
          })
          toast.success('Item removed successfully')
        } else {
          setData(updatedItem)
        }
      }
    } catch (error) {
      const err = error as AxiosError<Record<string, string>>
      console.error('Error deleting item:', err)
      toast.error(
        err?.response?.data?.message ||
          'Failed to delete item. Please try again.',
      )
    } finally {
      setDeleteModalOpen(false)
      setItemToDelete(null)
    }
  }
  // Handle delete modal open
  const onItemRemove = (value: CarouselItem) => {
    setItemToDelete(value)
    setDeleteModalOpen(true)
  }

  const processOrderUpdate = useCallback(
    async (newOrder: CarouselItem[]) => {
      try {
        const res = await mutateAsync({
          items: newOrder,
          updated_at: slides?.updated_at,
        })
        toast.success(res?.message)
      } catch (error) {
        const err = error as AxiosError<Record<string, string>>
        console.error('Error updating carousel order:', err)
        toast.error(
          err?.response?.data?.message ||
            'Failed to update carousel order. Please try again.',
        )
      } finally {
        await queryClient.invalidateQueries({
          queryKey: ['get-main-carousel-data'],
        })
      }
    },
    [mutateAsync, queryClient, slides?.updated_at],
  )

  // Debounced save function
  const debouncedSave = useCallback(
    (newOrder: CarouselItem[]) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(() => {
        void processOrderUpdate(newOrder)
      }, 1000)
    },
    [processOrderUpdate],
  )

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const isNewEntry = data[0]?.isNewEntry
    if (isNewEntry) {
      toast.error('Please save or remove the new entry before rearranging.')
      return
    }

    const { active, over } = event

    if (active.id !== over?.id) {
      setData(items => {
        const oldIndex = items.findIndex(item => item?.id === active.id)
        const newIndex = items.findIndex(item => item?.id === over?.id)

        const newOrder = arrayMove(items, oldIndex, newIndex)
        debouncedSave(newOrder)
        return newOrder
      })
    }

    setActiveId(null)
  }

  const items = useMemo(() => data.map(item => item?.id), [data])

  // Get the active item for drag overlay
  const activeItem = activeId ? data.find(item => item?.id === activeId) : null

  return (
    <>
      <div className="pb-8">
        <div className="h-full mt-0 pt-[88px]">
          <div className="text-white tc-mv-card-wrapper">
            <BaseHeader
              className="flex justify-between items-center"
              title="Carousel"
            >
              <button className="btn btn-primary" onClick={handleAddItem}>
                <i className="fa-solid fa-plus mr-2" />
                Add Item
              </button>
            </BaseHeader>
            <div className="mt-2 flex flex-col gap-4">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                cancelDrop={() => isLoading}
                onDragCancel={() => setActiveId(null)}
              >
                <SortableContext
                  items={items}
                  strategy={verticalListSortingStrategy}
                  disabled={isLoading}
                >
                  {data.map(item => (
                    <SortableCarouselEditCard
                      key={item?.id}
                      id={item?.id}
                      data={item}
                      collapsed={!item.isNewEntry}
                      onItemUpdate={handleItemUpdate}
                      onItemRemove={onItemRemove}
                    />
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeItem ? (
                    <CarouselEditCard
                      data={activeItem}
                      id={activeItem?.id}
                      collapsed={true}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        </div>
      </div>
      <DeleteModal
        open={deleteModalOpen}
        isLoading={isLoading}
        onClose={() => {
          setDeleteModalOpen(false)
          setItemToDelete(null)
        }}
        onDelete={handleDeleteItem}
      />
      {isLoading && <Loader label="" />}
    </>
  )
}
